/**
 * Centralized Logger Utility
 *
 * Environment-aware logging system that replaces console statements
 * - Development mode: Shows all logs
 * - Production mode: Only shows warnings and errors
 * - Debug mode: Shows detailed debug information when enabled
 */

class Logger {
  private isDev = process.env.NODE_ENV === 'development';
  private isDebug = process.env.NEXT_PUBLIC_DEBUG === 'true';
  private isServer = typeof window === 'undefined';

  /**
   * Debug logging - only shows when NEXT_PUBLIC_DEBUG=true
   */
  debug(message: string, data?: any) {
    if (this.isDebug) {
      const prefix = this.isServer ? '[SERVER][DEBUG]' : '[CLIENT][DEBUG]';
      console.log(`${prefix} ${message}`, data || '');
    }
  }

  /**
   * General logging - only shows in development
   */
  log(message: string, data?: any) {
    if (this.isDev) {
      const prefix = this.isServer ? '[SERVER]' : '[CLIENT]';
      console.log(`${prefix} ${message}`, data || '');
    }
  }

  /**
   * Info logging - similar to log but semantically different
   */
  info(message: string, data?: any) {
    if (this.isDev) {
      const prefix = this.isServer ? '[SERVER][INFO]' : '[CLIENT][INFO]';
      console.info(`ℹ️ ${prefix} ${message}`, data || '');
    }
  }

  /**
   * Warning logging - always shows
   */
  warn(message: string, data?: any) {
    const prefix = this.isServer ? '[SERVER][WARN]' : '[CLIENT][WARN]';
    console.warn(`⚠️ ${prefix} ${message}`, data || '');
  }

  /**
   * Error logging - always shows
   */
  error(message: string, error?: any, data?: any) {
    const prefix = this.isServer ? '[SERVER][ERROR]' : '[CLIENT][ERROR]';
    console.error(`❌ ${prefix} ${message}`, error || '', data || '');

    // In production, could send to error tracking service
    if (!this.isDev && error) {
      // TODO: Send to Sentry or similar service
      // captureException(error, { extra: { message, data } });
    }
  }

  /**
   * Group related logs together
   */
  group(label: string) {
    if (this.isDev) {
      console.group(label);
    }
  }

  /**
   * End a log group
   */
  groupEnd() {
    if (this.isDev) {
      console.groupEnd();
    }
  }

  /**
   * Log timing information
   */
  time(label: string) {
    if (this.isDev) {
      console.time(label);
    }
  }

  /**
   * End timing and log result
   */
  timeEnd(label: string) {
    if (this.isDev) {
      console.timeEnd(label);
    }
  }

  /**
   * Log a table for better visualization of data
   */
  table(data: any) {
    if (this.isDev) {
      console.table(data);
    }
  }

  /**
   * Clear the console (development only)
   */
  clear() {
    if (this.isDev) {
      console.clear();
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for dependency injection if needed
export type ILogger = Logger;